const MAX_VALUE = 999_999_999.99;
const MIN_VALUE = -999_999_999.99;

/**
 * Validate a string as numeric input for cash control fields.
 * Accepts: digits, single decimal point, leading negative sign, leading/trailing whitespace.
 * Rejects: any other characters.
 */
export function validateNumericInput(value: string): { valid: boolean; error?: string } {
  const trimmed = value.trim();
  
  // Empty is valid (treated as 0)
  if (trimmed === '') {
    return { valid: true };
  }
  
  // Check for valid numeric pattern: optional leading negative, digits, optional decimal with up to 2 digits
  const numericPattern = /^-?\d+(\.\d{0,2})?$/;
  if (!numericPattern.test(trimmed)) {
    // Allow trailing decimal (e.g., "123.") while typing
    const partialPattern = /^-?\d+\.?$/;
    if (partialPattern.test(trimmed)) {
      return { valid: true };
    }
    return { valid: false, error: 'Value must be a valid number' };
  }
  
  const num = parseFloat(trimmed);
  if (isNaN(num)) {
    return { valid: false, error: 'Value must be a valid number' };
  }
  
  if (num > MAX_VALUE || num < MIN_VALUE) {
    return { valid: false, error: `Value must be between ${MIN_VALUE.toLocaleString()} and ${MAX_VALUE.toLocaleString()}` };
  }
  
  return { valid: true };
}

/**
 * Parse a string to a numeric value. Empty/invalid strings return 0.
 */
export function parseNumericValue(value: string): number {
  const trimmed = value.trim();
  if (trimmed === '' || trimmed === '-') return 0;
  const num = parseFloat(trimmed);
  return isNaN(num) ? 0 : num;
}

/**
 * Check if a numeric value is within the allowed range.
 */
export function isInRange(value: number): boolean {
  return value >= MIN_VALUE && value <= MAX_VALUE;
}

/**
 * Format a number as currency display: commas as thousands separator, 2 decimal places.
 * Examples: 1234567.8 → "1,234,567.80", -500 → "-500.00"
 */
export function formatCurrency(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Trim and validate a string input in one call.
 * Returns the trimmed string, parsed numeric value, and validation result.
 */
export function trimAndValidate(value: string): {
  trimmed: string;
  numericValue: number;
  valid: boolean;
  error?: string;
} {
  const trimmed = value.trim();
  const validation = validateNumericInput(trimmed);
  const numericValue = parseNumericValue(trimmed);
  
  return {
    trimmed,
    numericValue,
    valid: validation.valid,
    error: validation.error,
  };
}
